import {
    useDashboardSocket,
} from "./hooks/useDashboardSocket.js";


import {
    StatusPill,
} from "./components/StatusPill.jsx";


import {
    MetricCard,
} from "./components/MetricCard.jsx";


import {
    MarketChart,
} from "./components/MarketChart.jsx";


import {
    SetupPanel,
} from "./components/SetupPanel.jsx";


import {
    GeminiAnalystPanel,
} from "./components/GeminiAnalystPanel.jsx";


import {
    MarketRadar,
} from "./components/MarketRadar.jsx";


import {
    GeminiTerminal,
} from "./components/GeminiTerminal.jsx";


import {
    PositionPanel,
} from "./components/PositionPanel.jsx";


import {
    EventFeed,
} from "./components/EventFeed.jsx";


const runtimeState = (
    health
) =>
    health?.tradingRuntime
        ?.state ??
    health?.tradingRuntime
        ?.status ??
    "UNKNOWN";


const marketState = (
    health
) =>
    health?.marketData
        ?.state ??
    health?.marketData
        ?.status ??
    "UNKNOWN";


const toneFor = (
    value
) => {
    const normalized =
        String(
            value ?? ""
        ).toUpperCase();


    if (
        [
            "RUNNING",
            "READY",
            "SCANNING",
            "OK",
            "CONNECTED",
        ].includes(
            normalized
        )
    ) {
        return "good";
    }


    if (
        [
            "PAUSED",
            "NOT_READY",
            "DEGRADED",
            "UNKNOWN",
        ].includes(
            normalized
        )
    ) {
        return "warn";
    }


    if (
        [
            "ERROR",
            "STOPPED",
            "DISCONNECTED",
            "EMERGENCY_MODE",
        ].includes(
            normalized
        )
    ) {
        return "danger";
    }


    return "neutral";
};


export default function App() {
    const {
        connected,

        health,

        candle,

        candidate,

        gemini,

        position,

        events,

        radarByPair,

        geminiMessages,

        sendGeminiQuery,
    } =
        useDashboardSocket();


    const runtime =
        runtimeState(
            health
        );


    const market =
        marketState(
            health
        );


    const redis =
        health?.redis ??
        "UNKNOWN";


    const telegram =
        health?.telegram?.enabled
            ? "ENABLED"
            : "DISABLED";


    return (
        <main className="min-h-screen text-slate-100">

            <div className="mx-auto max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8">

                <header className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-2xl shadow-black/20 backdrop-blur">

                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

                        <div>

                            <div className="flex items-center gap-3">

                                <div className="h-3 w-3 rounded-full bg-emerald-400 shadow-lg shadow-emerald-400/40" />

                                <h1 className="text-xl font-bold tracking-tight">
                                    SMC Trading Bot
                                </h1>

                            </div>


                            <p className="mt-1 text-sm text-slate-500">
                                SMC-1.0.0 · DEMO · Read-only operations dashboard
                            </p>

                        </div>


                        <div className="flex flex-wrap gap-2">

                            <StatusPill
                                label="Socket"
                                value={
                                    connected
                                        ? "CONNECTED"
                                        : "DISCONNECTED"
                                }
                                tone={
                                    connected
                                        ? "good"
                                        : "danger"
                                }
                            />


                            <StatusPill
                                label="Runtime"
                                value={
                                    runtime
                                }
                                tone={
                                    toneFor(
                                        runtime
                                    )
                                }
                            />


                            <StatusPill
                                label="Market"
                                value={
                                    market
                                }
                                tone={
                                    toneFor(
                                        market
                                    )
                                }
                            />


                            <StatusPill
                                label="Redis"
                                value={
                                    redis
                                }
                                tone={
                                    toneFor(
                                        redis
                                    )
                                }
                            />


                            <StatusPill
                                label="Telegram"
                                value={
                                    telegram
                                }
                                tone={
                                    telegram ===
                                    "ENABLED"
                                        ? "good"
                                        : "neutral"
                                }
                            />

                        </div>

                    </div>

                </header>


                <section className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

                    <MetricCard
                        label="Dashboard Clients"
                        value={
                            health
                                ?.dashboard
                                ?.clients ??
                            0
                        }
                        detail="Active Socket.IO clients"
                    />


                    <MetricCard
                        label="Heartbeat"
                        value={
                            health?.heartbeat ??
                            "UNKNOWN"
                        }
                        detail={
                            health?.timestamp
                                ? new Date(
                                      health.timestamp
                                  ).toLocaleString()
                                : "Waiting for health event"
                        }
                    />


                    <MetricCard
                        label="Latest Setup"
                        value={
                            candidate
                                ?.payload
                                ?.grade ??
                            candidate?.grade ??
                            "NONE"
                        }
                        detail="Deterministic arbitration result"
                    />


                    <MetricCard
                        label="Position State"
                        value={
                            position
                                ?.payload
                                ?.state ??
                            position
                                ?.payload
                                ?.status ??
                            "FLAT / UNKNOWN"
                        }
                        detail="Production lifecycle observer"
                    />

                </section>


                <section className="mb-6">

                    <MarketRadar
                        radarByPair={
                            radarByPair
                        }
                    />

                </section>


                <section className="mb-6 grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(360px,1fr)]">

                    <MarketChart
                        candle={
                            candle
                        }

                        candidate={
                            candidate
                        }

                        symbol={
                            candle?.symbol ??
                            candle
                                ?.payload
                                ?.symbol ??
                            "BTCUSDT"
                        }
                    />


                    <div className="space-y-6">

                        <SetupPanel
                            candidate={
                                candidate
                            }

                            gemini={
                                gemini
                            }
                        />


                        <GeminiAnalystPanel
                            gemini={
                                gemini
                            }
                        />


                        <GeminiTerminal
                            messages={
                                geminiMessages
                            }

                            onSubmit={
                                sendGeminiQuery
                            }

                            connected={
                                connected
                            }
                        />

                    </div>

                </section>


                <section className="mb-6">

                    <PositionPanel
                        position={
                            position
                        }
                    />

                </section>


                <section>

                    <EventFeed
                        events={
                            events
                        }
                    />

                </section>

            </div>

        </main>
    );
}